import React, { useEffect, useRef, useState } from "react";
import styles from "./ProfileForm.module.css";
import commonStyles from "components/commonStyle/commonStyle.module.css";
import { FaArrowAltCircleRight, FaRegTrashAlt } from "react-icons/fa";
import { EmergencyContact, CustomerProfileFormProps } from "modules/profile";
import ErrorFailed from "components/errorFailed/errorFailed";
import { setProfileInfo } from "services/profileManegement/set-profile-info";
import SuccessPopup from "components/successPopup/successPopup";
import { smoothScrollPolyfill } from "utils/smoothScroll";
import Popup from "components/Popup/popup";
import { GoInfo } from "react-icons/go";
import { getCost } from "services/cost/get-cost";


export default function ProfileForm({ profile_id, initialValues, buttonOptions = [], onSubmitSuccess }: CustomerProfileFormProps) {
    const [formValues, setFormValues] = useState(initialValues);
    const [costData, setCostData] = useState<{ id: string; category: string; price: number; }[]>([]);
    const [isPriseInfoOpen, setIsPriseInfoOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isSuccessPopupOpen, setIsSuccessPopupOpen] = useState(false);
    const [emergencyContacts, setEmergencyContacts] = useState<EmergencyContact[]>(initialValues.emergencyContacts);
    const [ibanParts, setIbanParts] = useState(["", "", "", "", "", ""]);
    const ibanRefs = useRef<(HTMLInputElement | null)[]>([]);

    const fetchData = async () => {
        try {
            const costs = await getCost();
            setCostData(costs);
        } catch (error) {
            console.error("Fehler beim Abrufen der daten:", error);
        }
    };

    useEffect(() => {
        fetchData();
        setFormValues(initialValues);
        if (initialValues.iban) {
            const iban = initialValues.iban.replace(/[^A-Z0-9]/g, "").toUpperCase();
            const parts = iban.match(/.{1,4}/g) || [];
            setIbanParts([...parts, "", "", "", "", "", ""].slice(0, 6));
        }
        setEmergencyContacts(initialValues.emergencyContacts);
    }, [initialValues]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        if (name === "phone") {
            if (/^[+\-\d\s]*$/.test(value)) {
                setFormValues({ ...formValues, [name]: value });
            }
        } else if (name === "zip") {
            if (/^\d{0,5}$/.test(value)) {
                setFormValues({ ...formValues, [name]: value });
            }
        } else {
            setFormValues({ ...formValues, [name]: value });
        }
    };
    const handleIbanPartChange = (index: number, value: string) => {
        const updatedParts = [...ibanParts];
        updatedParts[index] = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
        setIbanParts(updatedParts);
        if (value.length === 4 && index < ibanRefs.current.length - 1) {
            ibanRefs.current[index + 1]?.focus();
        }
    };


    const addEmergencyContact = () => {
        setEmergencyContacts([...emergencyContacts, { name: "", phone: "" }]);
    };

    const handleEmergencyContactChange = (index: number, field: "name" | "phone", value: string) => {
        const updatedContacts = [...emergencyContacts];
        if (field === "phone") {
            if (/^[+\-\d\s]*$/.test(value)) {
                updatedContacts[index][field] = value;
                setEmergencyContacts(updatedContacts);
            }
        } else {
            updatedContacts[index][field] = value;
            setEmergencyContacts(updatedContacts);
        }
    };

    const removeEmergencyContact = (index: number) => {
        if (emergencyContacts.length > 1) {
            const updatedContacts = emergencyContacts.filter((_, i) => i !== index);
            setEmergencyContacts(updatedContacts);
        }
    };



    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const iban = ibanParts.join("");
        const regex = /^[A-Z]{2}\d{20}$/;
        if (!regex.test(iban)) {
            setError("Die IBAN-Format ist falsch, bitte korrigieren Sie es.");
            smoothScrollPolyfill(150, 1000);
            return false;
        }
        const formElement = document.querySelector("form");
        if (formElement) {
            const firstInvalidElement = formElement.querySelector("input:invalid, select:invalid, textarea:invalid");
            if (firstInvalidElement) {

                const elementPosition = (firstInvalidElement as HTMLElement).getBoundingClientRect().top + window.scrollY;

                smoothScrollPolyfill(elementPosition - 200, 1000);
                (firstInvalidElement as HTMLElement).focus();
                setError("Bitte füllen Sie alle erforderlichen Felder mit gültigen Werten aus.");
                return false;
            }
        }
        const profileData = { ...formValues, iban, emergencyContacts };

        try {
            const response = await setProfileInfo(profileData, profile_id ?? null);
            if (response.success) {
                setIsSuccessPopupOpen(true)
            } else {
                setError(response.message)
                smoothScrollPolyfill(150, 1000);
                return false;

            }
        } catch (error) {
            console.error("Fehler beim Speichern der Profildaten:", error);
            setError('Fehler beim Speichern der Profildaten: Ein Serverfehler ist aufgetreten.')
            smoothScrollPolyfill(150, 1000);
        }
    };

    return (
        <form className={styles.form} onSubmit={handleSubmit} noValidate>
            {error && <ErrorFailed error={error} />}
            <section className={styles.section}>
                <div className={styles.sectionTitle}>
                    <h3>Familienstammdaten</h3>
                    <p>Die folgenden Angaben sind Pflichtfelder.</p>
                </div>
                <div className={commonStyles.formGroupRow}>
                    <div className={commonStyles.formGroup}>
                        <label htmlFor="first_name">Vorname*</label>
                        <input
                            type="text"
                            id="first_name"
                            name="first_name"
                            value={formValues.first_name}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    <div className={commonStyles.formGroup}>
                        <label htmlFor="last_name">Nachname*</label>
                        <input
                            type="text"
                            id="last_name"
                            name="last_name"
                            value={formValues.last_name}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                </div>
                <div className={commonStyles.formGroup}>
                    <label htmlFor="phone">Telefon*</label>
                    <input
                        type="text"
                        id="phone"
                        name="phone"
                        pattern="^\+?[0-9]{5,15}$"
                        value={formValues.phone}
                        onChange={handleInputChange}
                        required
                    />
                </div>
                <div className={commonStyles.formGroup}>
                    <label htmlFor="country">Land*</label>
                    <select
                        id="country"
                        name="country"
                        value={formValues.country}
                        onChange={handleInputChange}
                        required
                    >
                        <option value="Deutschland">Deutschland</option>
                    </select>
                </div>
                <div className={commonStyles.formGroupRow}>
                    <div className={commonStyles.formGroup}>
                        <label htmlFor="zip">PLZ*</label>
                        <input
                            type="text"
                            id="zip"
                            name="zip"
                            pattern="^\d{5}$"
                            value={formValues.zip}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    <div className={commonStyles.formGroup}>
                        <label htmlFor="city">Stadt*</label>
                        <input
                            type="text"
                            id="city"
                            name="city"
                            value={formValues.city}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                </div>
                <div className={commonStyles.formGroup}>
                    <label htmlFor="street">Straße & Haus Nr.*</label>
                    <input
                        type="text"
                        id="street"
                        name="street"
                        value={formValues.street}
                        onChange={handleInputChange}
                        required
                    />
                </div>
            </section>

            <section className={styles.section}>
                <div className={styles.sectionTitle}>
                    <h3>Status</h3>
                    <p>Bitte geben Sie Ihren Status an</p>
                </div>
                <div className={commonStyles.formGroup}>
                    <label htmlFor="status">Status auswählen*</label>
                    <select
                        id="status"
                        name="status"
                        value={formValues.status}
                        onChange={handleInputChange}
                        required
                    >
                        <option value="" disabled>auswählen...</option>
                        <option value="student">Studierende Elternteil</option>
                        <option value="university_staff">Mitarbeiter:innen der Universität Siegen</option>
                        <option value="studierendenwerk_staff">Mitarbeiter:innen des Studierendenwerks Siegen</option>
                        <option value="professor">Professor:in</option>
                        <option value="other">Dritte</option>
                    </select>
                </div>
                <div className={commonStyles.formGroup}>
                    <label htmlFor="faculty">Fakultät auswählen*</label>
                    <select
                        id="faculty"
                        name="faculty"
                        value={formValues.faculty}
                        onChange={handleInputChange}
                        required
                    >
                        <option value="" disabled>auswählen...</option>
                        <option value="1">Fakultät I: Philosophie</option>
                        <option value="2">Fakultät II: Bildung/Architektur/Künste</option>
                        <option value="3">Fakultät III: Wirtschaftswissenschaften</option>
                        <option value="4">Fakultät IV: Naturwissenschaften</option>
                        <option value="5">Fakultät V: Lebenswissenschaft</option>
                        <option value="other">Keine Angabe</option>
                    </select>
                </div>
            </section>

            <section className={styles.section}>
                <div className={styles.sectionTitle}>
                    <h3>Kontodaten  <GoInfo onClick={() => setIsPriseInfoOpen(true)} /></h3>
                    <p>Bitte geben Sie Ihre Kontodaten ein.</p>
                </div>
                <div className={commonStyles.formGroup}>
                    <label htmlFor="account_holder">Kontoinhaber*</label>
                    <input
                        type="text"
                        id="account_holder"
                        name="account_holder"
                        value={formValues.account_holder}
                        onChange={handleInputChange}
                        required
                    />
                </div>

                <div className={commonStyles.formGroup}>
                    <label>IBAN*</label>
                    <div className={commonStyles.formGroupRow}>
                        <input
                            type="text"
                            maxLength={4}
                            value={ibanParts[0]}
                            onChange={(e) => handleIbanPartChange(0, e.target.value)}
                            required
                        />
                        <input
                            ref={(el) => ibanRefs.current[1] = el}
                            type="text"
                            maxLength={4}
                            value={ibanParts[1]}
                            onChange={(e) => handleIbanPartChange(1, e.target.value)}
                            required
                        />
                        <input
                            ref={(el) => ibanRefs.current[2] = el}
                            type="text"
                            maxLength={4}
                            value={ibanParts[2]}
                            onChange={(e) => handleIbanPartChange(2, e.target.value)}
                            required
                        />
                        <input
                            ref={(el) => ibanRefs.current[3] = el}
                            type="text"
                            maxLength={4}
                            value={ibanParts[3]}
                            onChange={(e) => handleIbanPartChange(3, e.target.value)}
                            required
                        />
                        <input
                            ref={(el) => ibanRefs.current[4] = el}
                            type="text"
                            maxLength={4}
                            value={ibanParts[4]}
                            onChange={(e) => handleIbanPartChange(4, e.target.value)}
                            required
                        />
                        <input
                            ref={(el) => ibanRefs.current[5] = el}
                            type="text"
                            maxLength={2}
                            value={ibanParts[5]}
                            onChange={(e) => handleIbanPartChange(5, e.target.value)}
                            required
                        />
                    </div>
                </div>
                <div className={commonStyles.formGroup}>
                    <label htmlFor="bic">BIC</label>
                    <input
                        type="text"
                        id="bic"
                        name="bic"
                        value={formValues.bic}
                        onChange={handleInputChange}
                    />
                </div>
            </section>

            <section className={styles.section}>
                <div className={styles.sectionTitle}>
                    <h3>Notfallkontakte</h3>
                    <p>Bitte geben Sie alle Notfallkontakte an.</p>
                </div>
                {emergencyContacts.map((contact, index) => (
                    <div className={commonStyles.formGroupRow} key={index}>
                        <div className={commonStyles.formGroup}>
                            <label htmlFor={`emergencyName${index}`}>Name*</label>
                            <input
                                type="text"
                                id={`emergencyName${index}`}
                                name={`emergencyName${index}`}
                                value={contact.name}
                                onChange={(e) => handleEmergencyContactChange(index, "name", e.target.value)}
                                required
                            />
                        </div>
                        <div className={commonStyles.formGroup}>
                            <label htmlFor={`emergencyNumber${index}`}>Nummer*</label>
                            <input
                                type="text"
                                id={`emergencyNumber${index}`}
                                name={`emergencyNumber${index}`}
                                value={contact.phone}
                                pattern="^\+?[0-9]{5,15}$"
                                onChange={(e) => handleEmergencyContactChange(index, "phone", e.target.value)}
                                required
                            />
                        </div>
                        {emergencyContacts.length > 1 && (
                            <button
                                type="button"
                                className={styles.removeButton}
                                onClick={() => removeEmergencyContact(index)}
                            >
                                <FaRegTrashAlt />
                            </button>
                        )}
                    </div>
                ))}
                <div className={styles.addButtonContainer} >
                    <button
                        type="button"
                        className={`${commonStyles.commonButton} ${styles.addButton}`}
                        onClick={addEmergencyContact}
                    >
                        <span>Kontakt hinzufügen</span>
                        <FaArrowAltCircleRight className={`${commonStyles.commonButtonIcon} ${styles.buttonIcon}`} />
                    </button>
                </div>
            </section>

            <div className={styles.buttonContainer}>
                <div className={styles.leftButton}>
                    {buttonOptions[0] && (
                        <button
                            type={buttonOptions[0].onClick ? "button" : "submit"}
                            className={`${commonStyles.commonButton} ${commonStyles.backButton}`}
                            onClick={buttonOptions[0].onClick}
                        >
                            {buttonOptions[0].label}
                            {buttonOptions[0].icon && (
                                <span className={`${commonStyles.commonButtonIcon} ${styles.buttonIcon}`}>
                                    {buttonOptions[0].icon}
                                </span>
                            )}
                        </button>
                    )}
                </div>
                <div className={styles.rightButtons}>
                    {buttonOptions.slice(1).map((button, index) => (
                        <button
                            key={index}
                            type={button.onClick ? "button" : "submit"}
                            className={button.onClick ? `${commonStyles.commonButton} ${commonStyles.secondButton}` : `${commonStyles.commonButton} ${commonStyles.submitButton}`}
                            onClick={button.onClick}
                        >
                            {button.label}
                            {button.icon && (
                                <span className={`${commonStyles.commonButtonIcon} ${styles.buttonIcon}`}>
                                    {button.icon}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>
            {isPriseInfoOpen && (
                <Popup
                    title={"Informationen zu den Preisen"}
                    submitLabel={""}
                    closeLabel={""}
                    isWithButton={false}
                    isOpen={true}
                    onClose={() => { setIsPriseInfoOpen(false) }}
                >
                    <p>
                        Die Kosten der Flexi-Buchung, die sich auf einen Block/Doppelstunde (= 120 Minuten) und pro Kind beziehen, betragen:
                    </p>
                    <ul>
                        {costData.map((cost) => (
                            <li key={cost.id}>
                                für {cost.category}: {cost.price.toFixed(2)} €
                            </li>
                        ))}
                    </ul>

                </Popup>
            )}
            <SuccessPopup isPopupOpen={isSuccessPopupOpen} onClose={() => {
                if (onSubmitSuccess) {
                    onSubmitSuccess();
                }
                setError(null)
                setIsSuccessPopupOpen(false);
            }} />
        </form>
    );
}

